import { Node as CraftNode, NodeHelpersType, useEditor } from '@candulabs/craft';
import { styled } from 'canoe';
import React, { ReactElement } from 'react';

import { toCssSelector } from '@candu-ai/stylesheet/utils';
import { hiddenStyles } from 'commons/utils';
import Node from 'craft/components/Node/Node';
import Placeholder from 'craft/components/Placeholder';
import { BuiltInComponents } from 'craft/constants';
import { useNodeContext } from 'craft/hooks';
import { useFormAncestor } from 'craft/state';
import { hasDescendants } from 'craft/utils';

import { CheckIcon } from './CheckIcon';
import { SelectCardGroupToolbox } from './toolbox/SelectCardGroupToolbox';
import { SelectCardToolbox } from './toolbox/SelectCardToolbox';

const CheckWrapper = styled.div`
  position: absolute;
  z-index: 1;
  top: 8px;
  right: 8px;
`;

interface FormMultiSelectCardProps {
  children?: ReactElement | ReactElement[];
  value?: string;
}

export const FormMultiSelectCard = ({ children, value }: FormMultiSelectCardProps) => {
  const { id } = useNodeContext();
  const { shouldDisplayErrorState } = useFormAncestor(id);
  const hasChildren = hasDescendants(children);

  const { query } = useEditor();

  const parentNode = query.node(id).getParent();
  const { formInputId } = parentNode.get().data.props;
  const indexInParent = parentNode.childNodes().indexOf(id);

  const msCardClassName = toCssSelector('form', {
    output: 'html',
    className: 'msCard',
  });

  const checkboxDivClassName = toCssSelector('form', {
    output: 'html',
    className: 'msCardCheckboxDiv',
  });

  const elementId = `${formInputId}[${indexInParent}]`;
  const classNames = `${msCardClassName} ${shouldDisplayErrorState ? ' error' : ''}`.trim();

  return (
    <Node htmlFor={elementId} is="label" data-cy="node-multiselectcard" className={classNames}>
      <CheckWrapper>
        <input
          id={elementId}
          name={elementId}
          value={value}
          type="checkbox"
          disabled
          checked={indexInParent === 0}
          style={hiddenStyles as any}
        />
        <div className={checkboxDivClassName}>
          <CheckIcon />
        </div>
      </CheckWrapper>
      {hasChildren ? children : <Placeholder minWidth={0} />}
    </Node>
  );
};

FormMultiSelectCard.craft = {
  name: BuiltInComponents.FormMultiSelectCard,
  displayName: (node: CraftNode, helpers: NodeHelpersType) => {
    const parentNode = node.data.parent;
    return `Multi-select Card ${helpers(parentNode).childNodes().indexOf(node.id) + 1}`;
  },

  isCanvas: true,
  rules: {
    canDelete: (node: CraftNode, helpers: NodeHelpersType) => {
      const isLastRemaining = helpers(node.id).getParent().descendants().length === 1;
      return !isLastRemaining;
    },
    canDrop: (target: CraftNode) => target.data.name === BuiltInComponents.FormMultiSelectCardGroup,
  },
};

FormMultiSelectCard.toolbox = {
  component: [SelectCardToolbox, SelectCardGroupToolbox],
};

FormMultiSelectCard.selectedToolbar = {};
